import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { hasPermission } from "../lib/utils/permissions";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "replied", label: "Replied" },
  { value: "resolved", label: "Resolved" },
];

const DEPARTMENT_LABELS = {
  general: "General Inquiry",
  admission: "Admissions",
  appointment: "Appointment",
  careers: "Careers",
};

const STATUS_CLASSES = {
  new: "border-amber-200 bg-amber-50 text-amber-700",
  read: "border-blue-200 bg-blue-50 text-blue-700",
  replied: "border-violet-200 bg-violet-50 text-violet-700",
  resolved: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const formatDateTime = (value) => {
  if (!value) return "Date unavailable";
  const date = new Date(typeof value === "string" ? value.replace(" ", "T") : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold capitalize ${
      STATUS_CLASSES[status] || "border-slate-200 bg-slate-50 text-slate-600"
    }`}
  >
    {status || "new"}
  </span>
);

const ContactRequests = () => {
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canEdit = hasPermission(user, "contact-requests", "edit");
  const canDelete = hasPermission(user, "contact-requests", "delete");
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visiblePages = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [page, totalPages]);

  const fetchRequests = async (
    pageNumber = page,
    limit = pageSize,
    searchTerm = appliedSearch,
    status = statusFilter,
  ) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pageNumber),
        limit: String(limit),
      });
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (status !== "all") params.set("status", status);
      const response = await apiClient.get(`/contact-requests?${params.toString()}`);
      const responseData = response.data?.data || {};
      setRequests(responseData.requests || []);
      setTotal(Number(responseData.total) || 0);
      setPage(Number(responseData.page) || pageNumber);
      setPageSize(Number(responseData.limit) || limit);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load contact requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(1, 20, "", "all");
  }, []);

  const handleSearch = () => {
    const normalizedSearch = search.trim();
    setAppliedSearch(normalizedSearch);
    fetchRequests(1, pageSize, normalizedSearch, statusFilter);
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
    fetchRequests(1, pageSize, "", statusFilter);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    fetchRequests(1, pageSize, appliedSearch, value);
  };

  const handleStatusChange = async (requestId, status) => {
    setUpdatingStatus(requestId);
    try {
      await apiClient.patch(`/contact-requests/${requestId}/status`, { status });
      setRequests((current) =>
        current.map((item) => (item.id === requestId ? { ...item, status } : item)),
      );
      setSelectedRequest((current) =>
        current?.id === requestId ? { ...current, status } : current,
      );
      showSuccessToast("Contact request status updated");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleOpenRequest = (item) => {
    setSelectedRequest(item);
    if (item.status === "new" && canEdit) {
      handleStatusChange(item.id, "read");
    }
  };

  const handleDelete = async (requestId) => {
    try {
      await apiClient.delete(`/contact-requests/${requestId}`);
      showSuccessToast("Contact request deleted");
      if (selectedRequest?.id === requestId) setSelectedRequest(null);
      const nextPage = requests.length === 1 && page > 1 ? page - 1 : page;
      fetchRequests(nextPage, pageSize, appliedSearch, statusFilter);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete contact request");
    }
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    fetchRequests(nextPage, pageSize, appliedSearch, statusFilter);
  };

  const handlePageSizeChange = (value) => {
    const nextPageSize = Number(value);
    setPageSize(nextPageSize);
    fetchRequests(1, nextPageSize, appliedSearch, statusFilter);
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Contact Requests</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Review inquiries submitted from the public Contact Us form.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={handleStatusFilter}>
            <SelectTrigger className="h-10 w-40 rounded-lg" aria-label="Filter contact requests by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              placeholder="Search name, email, phone..."
              className="h-10 rounded-lg pl-10 pr-10"
              aria-label="Search contact requests"
            />
            {search ? (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Clear contact request search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <Button type="button" variant="outline" className="rounded-lg" onClick={handleSearch}>
            <Search className="mr-2 h-4 w-4" /> Search
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => fetchRequests(page, pageSize, appliedSearch, statusFilter)}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading contact requests...</div>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No contact requests found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Contact</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Department</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Message</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Status</th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map((item) => (
                    <tr key={item.id} className={item.status === "new" ? "bg-amber-50/30" : ""}>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="font-semibold text-slate-800">{item.name}</div>
                        <a className="mt-1 block hover:text-primary" href={`mailto:${item.email}`}>{item.email}</a>
                        <a className="block hover:text-primary" href={`tel:${item.contact_no}`}>{item.contact_no}</a>
                        <div className="mt-1 text-xs text-slate-400">{formatDateTime(item.created_at)}</div>
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-600">
                        {DEPARTMENT_LABELS[item.department] || item.department}
                      </td>
                      <td className="max-w-sm px-5 py-4 text-sm text-slate-600">
                        <p className="line-clamp-3 whitespace-pre-wrap">{item.message}</p>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        {canEdit ? (
                          <Select
                            value={item.status || "new"}
                            disabled={updatingStatus === item.id}
                            onValueChange={(value) => handleStatusChange(item.id, value)}
                          >
                            <SelectTrigger className="min-w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <StatusBadge status={item.status} />
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => handleOpenRequest(item)}>
                            <Eye className="mr-2 h-4 w-4" /> View
                          </Button>
                          {canDelete ? (
                            <DeleteConfirmationButton
                              title="Delete contact request?"
                              description="This inquiry will be permanently removed."
                              onConfirm={() => handleDelete(item.id)}
                            >
                              <Button type="button" variant="outline" size="sm" aria-label={`Delete request from ${item.name}`}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </DeleteConfirmationButton>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-slate-500">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total} requests
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Rows</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-9 w-20" aria-label="Contact requests per page"><SelectValue /></SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" className="h-9 rounded-lg px-3" onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || isLoading}>
                  <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                </Button>
                <div className="hidden items-center gap-1 md:flex">
                  {visiblePages.map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      type="button"
                      variant={pageNumber === page ? "default" : "outline"}
                      className="h-9 min-w-9 rounded-lg px-3"
                      onClick={() => handlePageChange(pageNumber)}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                </div>
                <Button type="button" variant="outline" className="h-9 rounded-lg px-3" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || isLoading}>
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={Boolean(selectedRequest)} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Request</DialogTitle>
            <DialogDescription>Submitted {formatDateTime(selectedRequest?.created_at)}</DialogDescription>
          </DialogHeader>
          {selectedRequest ? (
            <div className="space-y-5">
              <div className="grid gap-4 rounded-lg bg-slate-50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Name</p>
                  <p className="mt-1 font-semibold text-slate-800">{selectedRequest.name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Department</p>
                  <p className="mt-1 font-semibold text-slate-800">{DEPARTMENT_LABELS[selectedRequest.department] || selectedRequest.department}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Email</p>
                  <a className="mt-1 inline-flex items-center gap-2 font-medium text-primary hover:underline" href={`mailto:${selectedRequest.email}`}>
                    <Mail className="h-4 w-4" /> {selectedRequest.email}
                  </a>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Phone</p>
                  <a className="mt-1 inline-flex items-center gap-2 font-medium text-primary hover:underline" href={`tel:${selectedRequest.contact_no}`}>
                    <Phone className="h-4 w-4" /> {selectedRequest.contact_no}
                  </a>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Message</p>
                <p className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-100 bg-white p-4 text-sm leading-6 text-slate-700">{selectedRequest.message}</p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                {canEdit ? (
                  <Select value={selectedRequest.status || "new"} onValueChange={(value) => handleStatusChange(selectedRequest.id, value)} disabled={updatingStatus === selectedRequest.id}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : <StatusBadge status={selectedRequest.status} />}
                <div className="flex gap-2">
                  <a className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium transition hover:bg-gray-100" href={`tel:${selectedRequest.contact_no}`}>
                    <Phone className="mr-2 h-4 w-4" /> Call
                  </a>
                  <a className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-hover" href={`mailto:${selectedRequest.email}`}>
                    <Mail className="mr-2 h-4 w-4" /> Reply by email
                  </a>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactRequests;
